import React, { useState, useRef, useEffect } from "react";
import {
    CCard,
    CCardBody,
    CFormInput,
    CButton,
    CProgress,
    CAlert,
    CSpinner,
    CBadge,
    CFormSelect,
    CInputGroup,
    CInputGroupText,
    CForm,
    CFormLabel,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CTabs,
    CTabList,
    CTab,
    CTabContent,
    CTabPane
} from "@coreui/react";
import CIcon from '@coreui/icons-react';
import {
    cilCloudUpload,
    cilFolderOpen,
    cilXCircle,
    cilCheckCircle,
    cilWarning,
    cilImage,
    cilDataTransferDown,
    cilPlus,
    cilVideo,
    cilInfo,
    cilFolder,
    cilReload
} from '@coreui/icons';

const FileUploader = ({ ldoc, token }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [photoFolders, setPhotoFolders] = useState([]);
    const [videoFolders, setVideoFolders] = useState([]);
    const [selectedPhotoFolder, setSelectedPhotoFolder] = useState("");
    const [selectedVideoFolder, setSelectedVideoFolder] = useState("");
    const [showFolderModal, setShowFolderModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [activeTab, setActiveTab] = useState("auto");
    const [uploadQueue, setUploadQueue] = useState([]);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    
    const fileInputRef = useRef(null);
    const MAX_TOTAL_SIZE = 500 * 1024 * 1024;
    const MAX_FILE_SIZE = 1000 * 1024 * 1024;
    const MAX_ARCHIVE_SIZE = 500 * 1024 * 1024;

    // Диагностика в консоль
    const logDebug = (message, data = null) => {
        console.log(`[DEBUG ${new Date().toISOString()}] ${message}`, data || '');
    };

    const logError = (message, error = null) => {
        console.error(`[ERROR ${new Date().toISOString()}] ${message}`, error || '');
    };

    const getMediaPath = (workDir, mediaType = "photo") => {
        if (!workDir) return "";
        
        let cleanPath = workDir.trim();
        cleanPath = cleanPath.replace(/[\\/]+$/, '');
        
        logDebug("Очищенный WorkDir:", cleanPath);
        
        const pathParts = cleanPath.split(/[\\/]/);
        const targetFolder = mediaType === "photo" ? "Фото" : "Видео";
        
        if (pathParts.includes(targetFolder)) {
            const folderIndex = pathParts.indexOf(targetFolder);
            if (folderIndex < pathParts.length - 1) {
                return pathParts.slice(0, folderIndex + 1).join('\\');
            }
            return cleanPath;
        }
        
        return cleanPath + `\\${targetFolder}`;
    };

    const getBaseMediaPath = (mediaType = "photo") => {
        return new Promise(async (resolve, reject) => {
            try {
                logDebug(`Запрос данных объекта для ${mediaType}`, { ldoc });
                
                const response = await fetch(`/api/neural_network_module/object-data-by-ldoc/?ldoc=${ldoc}`, {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                logDebug(`Ответ API для ${mediaType}:`, {
                    status: response.status,
                    statusText: response.statusText,
                    ok: response.ok
                });
                
                if (response.ok) {
                    const data = await response.json();
                    logDebug(`Данные объекта для ${mediaType}:`, data);
                    
                    if (data.WorkDir) {
                        const mediaPath = getMediaPath(data.WorkDir, mediaType);
                        logDebug(`Сформированный путь к ${mediaType}:`, mediaPath);
                        resolve(mediaPath);
                    } else {
                        logError(`WorkDir не найден для ${mediaType}`);
                        reject(new Error("WorkDir не найден в данных объекта"));
                    }
                } else {
                    const errorText = await response.text();
                    logError(`Ошибка API для ${mediaType}:`, {
                        status: response.status,
                        error: errorText
                    });
                    reject(new Error("Не удалось получить данные объекта"));
                }
            } catch (error) {
                logError(`Исключение при получении пути для ${mediaType}:`, error);
                reject(error);
            }
        });
    };

    const loadFolders = async (mediaType = "photo", setFoldersFunction, setSelectedFolderFunction) => {
        try {
            const baseMediaPath = await getBaseMediaPath(mediaType);
            logDebug(`Базовый путь к ${mediaType}:`, baseMediaPath);
            
            if (!baseMediaPath) {
                logError(`Не удалось получить путь к папке ${mediaType}`);
                const defaultFolders = [`/${mediaType === "photo" ? "Фото" : "Видео"}/`];
                setFoldersFunction(defaultFolders);
                setSelectedFolderFunction(defaultFolders[0]);
                return;
            }
            
            const apiPath = baseMediaPath.replace(/\\/g, '/');
            
            try {
                logDebug(`Запрос содержимого папки ${mediaType}:`, { path: apiPath });
                
                const response = await fetch(`/api/ldoc/${ldoc}/workdir/?path=${encodeURIComponent(apiPath)}`, {
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                logDebug(`Ответ содержимого папки ${mediaType}:`, {
                    status: response.status,
                    statusText: response.statusText,
                    ok: response.ok
                });
                
                if (response.ok) {
                    const data = await response.json();
                    logDebug(`Содержимое папки ${mediaType}:`, data);
                    
                    const subfolders = data
                        .filter(item => item.isDirectory && item.path !== apiPath)
                        .map(item => item.path);
                    
                    const allFolders = [apiPath, ...subfolders];
                    
                    logDebug(`Все доступные папки (${mediaType}):`, allFolders);
                    
                    setFoldersFunction(allFolders);
                    
                    if (allFolders.length > 0) {
                        setSelectedFolderFunction(apiPath);
                    }
                } else {
                    logDebug(`Папка ${mediaType} еще не создана или нет доступа:`, apiPath);
                    const defaultFolders = [apiPath];
                    setFoldersFunction(defaultFolders);
                    setSelectedFolderFunction(apiPath);
                }
            } catch (apiError) {
                logError(`Ошибка загрузки содержимого папки ${mediaType}:`, apiError);
                const defaultFolders = [apiPath];
                setFoldersFunction(defaultFolders);
                setSelectedFolderFunction(apiPath);
            }
        } catch (error) {
            logError(`Общая ошибка загрузки папок (${mediaType}):`, error);
            const targetFolder = mediaType === "photo" ? "Фото" : "Видео";
            const defaultFolders = [`/${targetFolder}/`];
            setFoldersFunction(defaultFolders);
            setSelectedFolderFunction(`/${targetFolder}/`);
        }
    };

    useEffect(() => {
        if (ldoc && token) {
            console.log("=== ИНИЦИАЛИЗАЦИЯ FILEUPLOADER ===");
            console.log("LDoc:", ldoc);
            console.log("Token exists:", !!token);
            console.log("Token length:", token ? token.length : 0);
            console.log("Environment:", window.location.host);
            console.log("Is HTTPS:", window.location.protocol === 'https:');
            console.log("================================");
            
            loadFolders("photo", setPhotoFolders, setSelectedPhotoFolder);
            loadFolders("video", setVideoFolders, setSelectedVideoFolder);
        }
    }, [ldoc, token]);

    const isImageFile = (file) => {
        const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/tiff'];
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tiff'];
        return imageTypes.includes(file.type) || 
               imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    };

    const isVideoFile = (file) => {
        const videoTypes = ['video/mp4', 'video/mpeg', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm'];
        const videoExtensions = ['.mp4', '.mpeg', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.mpg', '.m4v'];
        return videoTypes.includes(file.type) || 
               videoExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    };

    const isArchiveFile = (file) => {
        return file.name.toLowerCase().endsWith('.zip');
    };

    const detectMediaType = (file) => {
        if (isImageFile(file)) return "photo";
        if (isVideoFile(file)) return "video";
        if (isArchiveFile(file)) {
            const name = file.name.toLowerCase();
            if (name.includes('photo') || name.includes('image') || name.includes('img') || name.includes('pic')) {
                return "photo";
            } else if (name.includes('video') || name.includes('mov') || name.includes('film')) {
                return "video";
            } else {
                return "photo";
            }
        }
        return null;
    };

    const validateArchiveFile = (file) => {
        const fileName = file.name.toLowerCase();
        
        // Проверка для ZIP архивов
        if (fileName.endsWith('.zip')) {
            if (file.size > MAX_ARCHIVE_SIZE) {
                return `ZIP архив слишком большой. Максимальный размер: ${formatFileSize(MAX_ARCHIVE_SIZE)}`;
            }
            
            if (file.size < 100) {
                return 'Файл слишком маленький для ZIP архива';
            }
            
            if (fileName.includes('password') || fileName.includes('protected') || fileName.includes('защищен')) {
                return 'Архивы с паролем не поддерживаются';
            }
            
            return null;
        }
        
        return null;
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        const items = Array.from(e.dataTransfer.items);
        const files = [];
        
        items.forEach(item => {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (file) files.push(file);
            }
        });
        
        handleFilesSelection(files);
    };

    const handleFileInput = (e) => {
        const files = Array.from(e.target.files);
        handleFilesSelection(files);
        e.target.value = '';
    };

    const checkTotalSize = (files) => {
        const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
        const currentTotalSize = selectedFiles.reduce((sum, fileInfo) => sum + fileInfo.file.size, 0);
        const totalSize = newFilesSize + currentTotalSize;
        
        if (totalSize > MAX_TOTAL_SIZE) {
            return `Общий размер файлов превышает максимальный лимит ${formatFileSize(MAX_TOTAL_SIZE)}. Текущий размер: ${formatFileSize(totalSize)}`;
        }
        return null;
    };

    const handleFilesSelection = (files) => {
        if (files.length === 0) return;

        logDebug("Начало обработки выбранных файлов:", {
            count: files.length,
            names: files.map(f => f.name),
            sizes: files.map(f => f.size)
        });

        const validFiles = files.filter(file => isImageFile(file) || isVideoFile(file) || isArchiveFile(file));
        
        if (validFiles.length === 0) {
            setErrorMessage("Выберите изображения (JPG, PNG, GIF, BMP), видео (MP4, AVI, MOV) или ZIP архивы");
            return;
        }

        const oversizedFiles = validFiles.filter(file => file.size > MAX_FILE_SIZE);
        if (oversizedFiles.length > 0) {
            setErrorMessage(`Некоторые файлы превышают максимальный размер ${formatFileSize(MAX_FILE_SIZE)}`);
            return;
        }

        // Проверка архивов
        const archiveErrors = validFiles
            .filter(file => isArchiveFile(file))
            .map(file => validateArchiveFile(file))
            .filter(error => error !== null);

        if (archiveErrors.length > 0) {
            setErrorMessage(archiveErrors[0]);
            return;
        }

        const sizeError = checkTotalSize(validFiles);
        if (sizeError) {
            setErrorMessage(sizeError);
            return;
        }

        const filesWithInfo = validFiles.map(file => ({
            file,
            mediaType: detectMediaType(file),
            selectedFolder: detectMediaType(file) === "photo" ? selectedPhotoFolder : selectedVideoFolder,
            status: "pending",
            retryCount: 0,
            error: null
        }));

        logDebug("Обработанные файлы:", filesWithInfo.map(f => ({
            name: f.file.name,
            size: f.file.size,
            type: f.file.type,
            mediaType: f.mediaType,
            isArchive: isArchiveFile(f.file)
        })));

        setSelectedFiles(prev => [...prev, ...filesWithInfo]);
        setUploadProgress(prev => [...prev, ...Array(validFiles.length).fill(0)]);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const handleCreateFolder = async (mediaType) => {
        if (!newFolderName.trim()) {
            setErrorMessage("Введите название папки");
            return;
        }

        const cleanFolderName = newFolderName.trim().replace(/[/\\]/g, '');
        
        if (!cleanFolderName) {
            setErrorMessage("Некорректное название папки");
            return;
        }

        setCreatingFolder(true);
        setErrorMessage("");

        try {
            const basePath = mediaType === "photo" 
                ? (selectedPhotoFolder.endsWith('/') ? selectedPhotoFolder.slice(0, -1) : selectedPhotoFolder)
                : (selectedVideoFolder.endsWith('/') ? selectedVideoFolder.slice(0, -1) : selectedVideoFolder);
            
            const newFolderPath = `${basePath}/${cleanFolderName}/`;
            
            logDebug("Создание новой папки:", {
                mediaType,
                basePath,
                newFolderPath,
                cleanFolderName
            });
            
            const currentFolders = mediaType === "photo" ? photoFolders : videoFolders;
            if (currentFolders.includes(newFolderPath)) {
                setErrorMessage(`Папка "${cleanFolderName}" уже существует`);
                setCreatingFolder(false);
                return;
            }
            
            const response = await fetch(`/api/ldoc/${ldoc}/workdir/create-folder/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    path: newFolderPath
                })
            });
            
            logDebug("Ответ создания папки:", {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
            });
            
            if (response.ok) {
                if (mediaType === "photo") {
                    const updatedFolders = [...photoFolders, newFolderPath];
                    setPhotoFolders(updatedFolders);
                    setSelectedPhotoFolder(newFolderPath);
                } else {
                    const updatedFolders = [...videoFolders, newFolderPath];
                    setVideoFolders(updatedFolders);
                    setSelectedVideoFolder(newFolderPath);
                }
                
                setShowFolderModal(false);
                setNewFolderName("");
                
                setSuccessMessage(`Папка "${cleanFolderName}" успешно создана`);
                setTimeout(() => setSuccessMessage(""), 3000);
            } else {
                const errorData = await response.json();
                setErrorMessage(`Ошибка создания папки: ${errorData.error || 'Неизвестная ошибка'}`);
            }
            
        } catch (error) {
            logError("Ошибка создания папки:", error);
            setErrorMessage(`Ошибка создания папки: ${error.message}`);
        } finally {
            setCreatingFolder(false);
        }
    };

    const sendFilesUploadedEvent = () => {
        const event = new CustomEvent('filesUploaded', {
            detail: {
                ldoc: ldoc,
                timestamp: Date.now()
            }
        });
        window.dispatchEvent(event);
    };

    const uploadSingleFileWithRetry = async (fileInfo, index, retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                logDebug(`Попытка ${attempt}/${retries} для файла ${fileInfo.file.name}`);
                
                if (attempt > 1) {
                    // Обновляем статус файла
                    setSelectedFiles(prev => prev.map((item, idx) => 
                        idx === index ? { ...item, status: "retrying", retryCount: attempt - 1 } : item
                    ));
                    
                    // Пауза перед повторной попыткой
                    await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
                }
                
                if (isArchiveFile(fileInfo.file)) {
                    return await uploadArchiveFile(fileInfo, index);
                } else {
                    return await uploadSingleFile(fileInfo, index);
                }
                
            } catch (error) {
                logError(`Попытка ${attempt} не удалась для ${fileInfo.file.name}:`, error);
                
                if (attempt === retries) {
                    setSelectedFiles(prev => prev.map((item, idx) => 
                        idx === index ? { ...item, status: "error", error: error.message } : item
                    ));
                    throw error;
                }
            }
        }
    };

    const uploadSingleFile = async (fileInfo, index) => {
        return new Promise((resolve, reject) => {
            const mediaType = activeTab === "auto" ? fileInfo.mediaType : activeTab;
            
            const formData = new FormData();
            formData.append('file', fileInfo.file);
            
            const targetFolder = mediaType === "photo" ? selectedPhotoFolder : selectedVideoFolder;
            formData.append('path', targetFolder);

            logDebug(`Подготовка загрузки файла (${mediaType}):`, {
                fileName: fileInfo.file.name,
                fileSize: fileInfo.file.size,
                fileType: fileInfo.file.type,
                targetFolder,
                isArchive: isArchiveFile(fileInfo.file)
            });

            const xhr = new XMLHttpRequest();
            let uploadStarted = false;
            let lastProgressTime = 0;
            
            xhr.upload.addEventListener('progress', (event) => {
                if (!uploadStarted) {
                    uploadStarted = true;
                    logDebug(`Началась загрузка файла ${fileInfo.file.name}:`, {
                        lengthComputable: event.lengthComputable,
                        total: event.total,
                        loaded: event.loaded
                    });
                }
                
                // Ограничиваем частоту обновления прогресса
                const now = Date.now();
                if (now - lastProgressTime > 100) { // Обновляем каждые 100мс
                    lastProgressTime = now;
                    if (event.lengthComputable) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100);
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[index] = percentComplete;
                            return newProgress;
                        });
                        
                        setSelectedFiles(prev => prev.map((item, idx) => 
                            idx === index ? { ...item, status: "uploading" } : item
                        ));
                    }
                }
            });

            xhr.addEventListener('readystatechange', () => {
                if (xhr.readyState === 4) {
                    logDebug(`ReadyState для ${fileInfo.file.name}:`, {
                        readyState: xhr.readyState,
                        status: xhr.status,
                        stateText: xhr.statusText
                    });
                }
            });

            xhr.addEventListener('load', () => {
                logDebug(`Ответ сервера для ${fileInfo.file.name}:`, {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    readyState: xhr.readyState
                });
                
                try {
                    let responseData;
                    if (xhr.responseText) {
                        try {
                            responseData = JSON.parse(xhr.responseText);
                        } catch (e) {
                            logError(`Ошибка парсинга JSON для ${fileInfo.file.name}:`, xhr.responseText);
                            responseData = { error: "Некорректный ответ сервера" };
                        }
                    }
                    
                    if (xhr.status >= 200 && xhr.status < 300) {
                        logDebug(`Файл ${fileInfo.file.name} успешно загружен`, responseData);
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[index] = 100;
                            return newProgress;
                        });
                        
                        setSelectedFiles(prev => prev.map((item, idx) => 
                            idx === index ? { ...item, status: "success" } : item
                        ));
                        
                        resolve();
                    } else {
                        logError(`Ошибка сервера для ${fileInfo.file.name}:`, responseData || xhr.responseText);
                        reject(new Error(responseData?.error || `Ошибка сервера: ${xhr.status}`));
                    }
                } catch (error) {
                    logError(`Исключение при обработке ответа для ${fileInfo.file.name}:`, error);
                    reject(new Error(`Ошибка загрузки: ${xhr.status} - ${xhr.statusText}`));
                }
            });

            xhr.addEventListener('error', (event) => {
                logError(`Ошибка сети при загрузке ${fileInfo.file.name}:`, event);
                reject(new Error('Ошибка сети при загрузке файла'));
            });

            xhr.addEventListener('abort', () => {
                logDebug(`Загрузка отменена для ${fileInfo.file.name}`);
                reject(new Error('Загрузка отменена'));
            });

            // Увеличиваем время ожидания для больших файлов
            xhr.timeout = fileInfo.file.size > 100 * 1024 * 1024 ? 300000 : 120000; // 5 мин для >100MB, 2 мин для остальных
            
            xhr.open('POST', `/api/ldoc/${ldoc}/workdir/upload/`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            
            // Добавляем заголовок с размером файла
            xhr.setRequestHeader('X-File-Size', fileInfo.file.size.toString());
            
            logDebug(`Отправка запроса для ${fileInfo.file.name} (размер: ${formatFileSize(fileInfo.file.size)})`);
            
            xhr.send(formData);
        });
    };

    const uploadArchiveFile = async (fileInfo, index) => {
        return new Promise((resolve, reject) => {
            logDebug(`Специальная загрузка ZIP архива: ${fileInfo.file.name}`);
            
            const mediaType = activeTab === "auto" ? fileInfo.mediaType : activeTab;
            const fileName = fileInfo.file.name.toLowerCase();
            
            const formData = new FormData();
            formData.append('file', fileInfo.file);
            formData.append('extract', 'true');
            formData.append('delete_archive', 'true');
            formData.append('preserve_structure', 'true');
            formData.append('chunked_upload', 'true');
            
            const targetFolder = mediaType === "photo" ? selectedPhotoFolder : selectedVideoFolder;
            formData.append('path', targetFolder);

            const xhr = new XMLHttpRequest();
            
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const percentComplete = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(prev => {
                        const newProgress = [...prev];
                        newProgress[index] = percentComplete;
                        return newProgress;
                    });
                    
                    setSelectedFiles(prev => prev.map((item, idx) => 
                        idx === index ? { ...item, status: "uploading" } : item
                    ));
                    
                    logDebug(`Прогресс архива ${fileInfo.file.name}: ${percentComplete}%`);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    try {
                        const responseData = JSON.parse(xhr.responseText);
                        logDebug(`ZIP архив ${fileInfo.file.name} загружен успешно`, responseData);
                        
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[index] = 100;
                            return newProgress;
                        });
                        
                        setSelectedFiles(prev => prev.map((item, idx) => 
                            idx === index ? { ...item, status: "success" } : item
                        ));
                        
                        resolve();
                    } catch (error) {
                        logError(`Ошибка парсинга ответа архива ${fileInfo.file.name}:`, error);
                        reject(new Error('Некорректный ответ сервера'));
                    }
                } else {
                    let errorMessage = `Ошибка загрузки архива: ${xhr.status}`;
                    try {
                        const errorData = JSON.parse(xhr.responseText);
                        errorMessage = errorData.error || errorMessage;
                    } catch (e) {
                        // Игнорируем ошибку парсинга
                    }
                    
                    logError(`Ошибка ZIP архива ${fileInfo.file.name}:`, xhr.responseText);
                    reject(new Error(errorMessage));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Ошибка сети при загрузке архива'));
            });

            xhr.addEventListener('abort', () => {
                reject(new Error('Загрузка архива отменена'));
            });

            xhr.open('POST', `/api/ldoc/${ldoc}/workdir/upload/`);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.setRequestHeader('X-File-Size', fileInfo.file.size.toString());
            xhr.setRequestHeader('X-File-Name', encodeURIComponent(fileInfo.file.name));
            xhr.setRequestHeader('X-Archive-Type', 'zip');
            
            // Увеличиваем таймаут для архивов
            xhr.timeout = 600000; // 10 минут
            
            xhr.send(formData);
        });
    };

    const uploadFiles = async () => {
        if (selectedFiles.length === 0) {
            setErrorMessage("Выберите файлы для загрузки");
            return;
        }

        if (!ldoc) {
            setErrorMessage("Не указан номер заключения");
            return;
        }

        if (!token) {
            setErrorMessage("Токен авторизации отсутствует");
            return;
        }

        console.log("=== НАЧАЛО ЗАГРУЗКИ ФАЙЛОВ ===");
        console.log("Количество файлов:", selectedFiles.length);
        console.log("LDoc:", ldoc);
        console.log("Активная вкладка:", activeTab);
        console.log("============================");

        setUploading(true);
        setErrorMessage("");
        setSuccessMessage("");

        let uploadedCount = 0;
        let errors = [];
        
        // Сначала загружаем обычные файлы, потом архивы
        const archiveFiles = selectedFiles.filter(fileInfo => isArchiveFile(fileInfo.file));
        const regularFiles = selectedFiles.filter(fileInfo => !isArchiveFile(fileInfo.file));
        
        logDebug("Разделение файлов:", {
            archives: archiveFiles.length,
            regular: regularFiles.length
        });

        // Загружаем обычные файлы
        for (let i = 0; i < regularFiles.length; i++) {
            try {
                const fileIndex = selectedFiles.findIndex(f => f.file === regularFiles[i].file);
                await uploadSingleFileWithRetry(regularFiles[i], fileIndex, 2);
                uploadedCount++;
                logDebug(`Обычный файл ${regularFiles[i].file.name} успешно загружен`);
            } catch (error) {
                errors.push(regularFiles[i].file.name);
                logError(`Ошибка загрузки файла ${regularFiles[i].file.name}:`, error);
            }
        }
        
        // Делаем паузу перед загрузкой архивов
        if (archiveFiles.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Загружаем архивы по одному
            for (let i = 0; i < archiveFiles.length; i++) {
                try {
                    const fileIndex = selectedFiles.findIndex(f => f.file === archiveFiles[i].file);
                    logDebug(`Загрузка архива ${i+1}/${archiveFiles.length}:`, archiveFiles[i].file.name);
                    
                    await uploadSingleFileWithRetry(archiveFiles[i], fileIndex, 3);
                    uploadedCount++;
                    
                    // Делаем паузу между архивами
                    if (i < archiveFiles.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                } catch (error) {
                    errors.push(archiveFiles[i].file.name);
                    logError(`Ошибка загрузки архива ${archiveFiles[i].file.name}:`, error);
                }
            }
        }

        setUploading(false);
        
        if (errors.length > 0) {
            const errorMsg = `Ошибка загрузки ${errors.length} файлов: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`;
            setErrorMessage(errorMsg);
        }
        
        if (uploadedCount > 0) {
            const photoCount = selectedFiles.filter(f => f.mediaType === "photo").length;
            const videoCount = selectedFiles.filter(f => f.mediaType === "video").length;
            const archiveCount = selectedFiles.filter(f => isArchiveFile(f.file)).length;
            
            let successText = `Успешно загружено ${uploadedCount} файлов`;
            let details = [];
            if (photoCount > 0) details.push(`${photoCount} фото`);
            if (videoCount > 0) details.push(`${videoCount} видео`);
            if (archiveCount > 0) details.push(`${archiveCount} ZIP архивов`);
            
            if (details.length > 0) {
                successText += `: ${details.join(', ')}`;
            }
            
            setSuccessMessage(successText);
            
            sendFilesUploadedEvent();
            
            setTimeout(() => {
                // Очищаем только успешно загруженные файлы
                setSelectedFiles(prev => prev.filter(fileInfo => 
                    fileInfo.status !== "success" && fileInfo.status !== "error"
                ));
                setUploadProgress(prev => prev.filter((_, i) => 
                    selectedFiles[i]?.status !== "success" && selectedFiles[i]?.status !== "error"
                ));
                if (errors.length === 0) {
                    setSuccessMessage("");
                }
            }, 5000);
        }
        
        console.log("=== ЗАВЕРШЕНИЕ ЗАГРУЗКИ ===");
        console.log("Загружено:", uploadedCount);
        console.log("Ошибки:", errors.length);
        console.log("=========================");
    };

    const retryFileUpload = async (index) => {
        try {
            setErrorMessage("");
            setSelectedFiles(prev => prev.map((item, idx) => 
                idx === index ? { ...item, status: "retrying", error: null } : item
            ));
            
            setUploadProgress(prev => {
                const newProgress = [...prev];
                newProgress[index] = 0;
                return newProgress;
            });
            
            await uploadSingleFileWithRetry(selectedFiles[index], index, 2);
            
            setSuccessMessage(`Файл ${selectedFiles[index].file.name} успешно перезагружен`);
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            setErrorMessage(`Ошибка перезагрузки файла: ${error.message}`);
        }
    };

    const removeFile = (index) => {
        logDebug("Удаление файла из списка:", {
            index,
            fileName: selectedFiles[index]?.file.name
        });
        
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setUploadProgress(prev => prev.filter((_, i) => i !== index));
        setErrorMessage("");
    };

    const clearAllFiles = () => {
        logDebug("Очистка всех выбранных файлов:", {
            count: selectedFiles.length
        });
        
        setSelectedFiles([]);
        setUploadProgress([]);
        setErrorMessage("");
        setSuccessMessage("");
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getFileIcon = (mediaType) => {
        if (mediaType === "photo") {
            return <CIcon icon={cilImage} className="me-2 text-primary" />;
        } else if (mediaType === "video") {
            return <CIcon icon={cilVideo} className="me-2 text-danger" />;
        } else {
            return <CIcon icon={cilFolder} className="me-2 text-muted" />;
        }
    };

    const getFileTypeText = (fileInfo) => {
        if (isArchiveFile(fileInfo.file)) {
            return "ZIP архив";
        }
        if (isImageFile(fileInfo.file)) return "Изображение";
        if (isVideoFile(fileInfo.file)) return "Видео";
        return "Файл";
    };

    const getStatusBadge = (fileInfo, index) => {
        const status = fileInfo.status || "pending";
        const progress = uploadProgress[index] || 0;
        
        if (status === "success" || progress === 100) {
            return <CBadge color="success" className="me-2">Успешно</CBadge>;
        }
        
        if (status === "error" || progress === -1) {
            return <CBadge color="danger" className="me-2">Ошибка</CBadge>;
        }
        
        if (status === "uploading" && progress > 0 && progress < 100) {
            return <CBadge color="info" className="me-2">{progress}%</CBadge>;
        }
        
        if (status === "retrying") {
            return <CBadge color="warning" className="me-2">Повтор...</CBadge>;
        }
        
        return <CBadge color="secondary" className="me-2">В очереди</CBadge>;
    };

    const totalSize = selectedFiles.reduce((sum, fileInfo) => sum + fileInfo.file.size, 0);
    const photoCount = selectedFiles.filter(f => f.mediaType === "photo").length;
    const videoCount = selectedFiles.filter(f => f.mediaType === "video").length;
    const archiveCount = selectedFiles.filter(f => isArchiveFile(f.file)).length;

    return (
        <div>
            <CCard className="mb-4">
                <CCardBody>
                    <CTabs activeTab={activeTab} onActiveTabChange={setActiveTab} className="mb-4">
                        <CTabContent>
                            <CTabPane tab="auto" className="mt-3">
                                <div className="alert alert-info">
                                    <CIcon icon={cilInfo} className="me-2" />
                                    Система автоматически определит тип файла и сохранит его в соответствующую папку.
                                </div>
                            </CTabPane>
                            
                            <CTabPane tab="photo" className="mt-3">
                                <div className="alert alert-primary">
                                    <CIcon icon={cilImage} className="me-2" />
                                    Все файлы будут сохранены в папку "Фото".
                                </div>
                            </CTabPane>
                            
                            <CTabPane tab="video" className="mt-3">
                                <div className="alert alert-danger">
                                    <CIcon icon={cilVideo} className="me-2" />
                                    Все файлы будут сохранены в папку "Видео".
                                </div>
                            </CTabPane>
                        </CTabContent>
                    </CTabs>

                    {errorMessage && (
                        <CAlert color="danger" className="mb-3">
                            <CIcon icon={cilWarning} className="me-2" />
                            {errorMessage}
                        </CAlert>
                    )}
                    
                    {successMessage && (
                        <CAlert color="success" className="mb-3">
                            <CIcon icon={cilCheckCircle} className="me-2" />
                            {successMessage}
                        </CAlert>
                    )}

                    {archiveCount > 0 && (
                        <CAlert color="warning" className="mb-3">
                            <CIcon icon={cilWarning} className="me-2" />
                            <strong>Внимание:</strong> В выбранных файлах есть {archiveCount} ZIP архив(ов). 
                            Загрузка архивов может занять больше времени. Убедитесь, что архивы не защищены паролем.
                        </CAlert>
                    )}

                    {(activeTab === "photo" || activeTab === "video") && (
                        <div className="mb-4">
                            <CFormLabel className="fw-medium mb-2">
                                Папка для загрузки ({activeTab === "photo" ? "Фото" : "Видео"}):
                            </CFormLabel>
                            <div className="d-flex gap-2">
                                <CInputGroup className="flex-grow-1">
                                    <CInputGroupText>
                                        <CIcon icon={cilFolderOpen} />
                                    </CInputGroupText>
                                    <CFormSelect 
                                        value={activeTab === "photo" ? selectedPhotoFolder : selectedVideoFolder}
                                        onChange={(e) => {
                                            if (activeTab === "photo") {
                                                setSelectedPhotoFolder(e.target.value);
                                            } else {
                                                setSelectedVideoFolder(e.target.value);
                                            }
                                        }}
                                        disabled={uploading}
                                    >
                                        {(activeTab === "photo" ? photoFolders : videoFolders).length === 0 ? (
                                            <option value="">Загрузка папок...</option>
                                        ) : (
                                            (activeTab === "photo" ? photoFolders : videoFolders).map((folder, index) => (
                                                <option key={index} value={folder}>
                                                    {folder}
                                                </option>
                                            ))
                                        )}
                                    </CFormSelect>
                                </CInputGroup>
                                
                                <CButton 
                                    color="secondary" 
                                    onClick={() => setShowFolderModal(true)}
                                    disabled={uploading || (activeTab === "photo" ? !selectedPhotoFolder : !selectedVideoFolder)}
                                    variant="outline"
                                >
                                    <CIcon icon={cilPlus} />
                                </CButton>
                            </div>
                        </div>
                    )}

                    <div 
                        className={`drag-drop-area p-5 text-center border rounded mb-4 ${dragActive ? 'border-primary bg-primary bg-opacity-5' : 'border-dashed'} ${uploading ? 'opacity-50' : ''}`}
                        onDragEnter={!uploading ? handleDrag : undefined}
                        onDragOver={!uploading ? handleDrag : undefined}
                        onDragLeave={!uploading ? handleDrag : undefined}
                        onDrop={!uploading ? handleDrop : undefined}
                        style={{ 
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            backgroundColor: dragActive ? 'var(--cui-primary-bg)' : 'var(--cui-secondary-bg)'
                        }}
                        onClick={!uploading ? () => fileInputRef.current?.click() : undefined}
                    >
                        <CIcon icon={cilCloudUpload} size="xxl" className="mb-3" />
                        <h5 className="mb-2">
                            {activeTab === "auto" 
                                ? "Перетащите фото, видео или ZIP архивы сюда" 
                                : activeTab === "photo" 
                                    ? "Перетащите фото или ZIP архивы сюда" 
                                    : "Перетащите видео или ZIP архивы сюда"}
                        </h5>
                        <p className="text-muted mb-3">или нажмите для выбора файлов</p>
                        
                        <div className="d-flex justify-content-center gap-2 flex-wrap mb-3">
                            <CBadge color="primary" className="px-3 py-2">
                                <CIcon icon={cilImage} className="me-1" /> Изображения
                            </CBadge>
                            <CBadge color="danger" className="px-3 py-2">
                                <CIcon icon={cilVideo} className="me-1" /> Видео
                            </CBadge>
                            <CBadge color="warning" className="px-3 py-2">
                                <CIcon icon={cilDataTransferDown} className="me-1" /> ZIP архивы
                            </CBadge>
                        </div>
                    </div>
                    
                    <CFormInput 
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileInput}
                        multiple
                        className="d-none"
                        accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.mp4,.mpeg,.avi,.mov,.wmv,.flv,.webm,.mkv,.mpg,.m4v,.zip"
                    />

                    {selectedFiles.length > 0 && (
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h6 className="mb-1">Выбрано файлов: {selectedFiles.length}</h6>
                                    <small className="text-muted">
                                        {photoCount} фото, {videoCount} видео, {archiveCount} ZIP архивов • {formatFileSize(totalSize)}
                                    </small>
                                </div>
                                <div className="d-flex gap-2">
                                    <CButton 
                                        color="danger" 
                                        size="sm"
                                        onClick={clearAllFiles}
                                        disabled={uploading}
                                        variant="outline"
                                    >
                                        <CIcon icon={cilXCircle} className="me-1" />
                                        Очистить все
                                    </CButton>
                                </div>
                            </div>
                            
                            <div className="selected-files-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {selectedFiles.map((fileInfo, index) => {
                                    const progress = uploadProgress[index] || 0;
                                    const isError = fileInfo.status === "error" || progress === -1;
                                    const isSuccess = fileInfo.status === "success" || progress === 100;
                                    const isUploading = fileInfo.status === "uploading" && progress > 0 && progress < 100;
                                    const mediaType = activeTab === "auto" ? fileInfo.mediaType : activeTab;
                                    
                                    return (
                                        <div 
                                            key={`${fileInfo.file.name}-${index}`} 
                                            className={`d-flex align-items-center p-3 border rounded mb-2 ${isError ? 'border-danger bg-danger bg-opacity-5' : isSuccess ? 'border-success bg-success bg-opacity-5' : ''}`}
                                        >
                                            <div className="flex-shrink-0">
                                                {getFileIcon(mediaType)}
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="me-3" style={{ minWidth: 0 }}>
                                                        <div className="d-flex align-items-center mb-1">
                                                            <div className="fw-medium text-truncate" style={{ maxWidth: '250px' }}>
                                                                {fileInfo.file.name}
                                                            </div>
                                                            {getStatusBadge(fileInfo, index)}
                                                        </div>
                                                        <div className="d-flex align-items-center flex-wrap">
                                                            <small className="text-muted me-2">
                                                                {formatFileSize(fileInfo.file.size)}
                                                            </small>
                                                            <CBadge color={mediaType === "photo" ? "primary" : "danger"} className="me-2">
                                                                {mediaType === "photo" ? "Фото" : "Видео"}
                                                            </CBadge>
                                                            <CBadge color={isArchiveFile(fileInfo.file) ? "warning" : "secondary"} className="me-2">
                                                                {getFileTypeText(fileInfo)}
                                                            </CBadge>
                                                        </div>
                                                        {fileInfo.error && (
                                                            <div className="text-danger small mt-1">
                                                                <CIcon icon={cilWarning} className="me-1" />
                                                                {fileInfo.error}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        {isUploading && (
                                                            <div className="me-3" style={{ width: '100px' }}>
                                                                <CProgress 
                                                                    value={progress} 
                                                                    size="sm"
                                                                    color="primary"
                                                                    className="mb-1"
                                                                />
                                                                <small className="text-muted">{progress}%</small>
                                                            </div>
                                                        )}
                                                        
                                                        <div className="d-flex gap-1">
                                                            {isError && !uploading && (
                                                                <CButton 
                                                                    color="warning" 
                                                                    size="sm"
                                                                    onClick={() => retryFileUpload(index)}
                                                                    variant="ghost"
                                                                >
                                                                    <CIcon icon={cilReload} />
                                                                </CButton>
                                                            )}
                                                            
                                                            {!uploading && !isSuccess && (
                                                                <CButton 
                                                                    color="danger" 
                                                                    size="sm"
                                                                    onClick={() => removeFile(index)}
                                                                    variant="ghost"
                                                                >
                                                                    <CIcon icon={cilXCircle} />
                                                                </CButton>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="mt-4 d-flex justify-content-end gap-2">
                        <CButton 
                            color="secondary" 
                            onClick={clearAllFiles}
                            disabled={selectedFiles.length === 0 || uploading}
                            size="sm"
                            variant="outline"
                        >
                            <CIcon icon={cilXCircle} className="me-2" />
                            Очистить
                        </CButton>
                        
                        <CButton 
                            color="primary" 
                            onClick={uploadFiles}
                            disabled={selectedFiles.length === 0 || uploading}
                            size="sm"
                        >
                            {uploading ? (
                                <>
                                    <CSpinner size="sm" className="me-2" />
                                    Загрузка...
                                </>
                            ) : (
                                <>
                                    <CIcon icon={cilCloudUpload} className="me-2" />
                                    Загрузить ({selectedFiles.length})
                                </>
                            )}
                        </CButton>
                    </div>
                </CCardBody>
            </CCard>

            <CModal 
                visible={showFolderModal} 
                onClose={() => {
                    setShowFolderModal(false);
                    setNewFolderName("");
                }}
            >
                <CModalHeader>
                    <CModalTitle>Создание новой папки</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <p className="text-muted mb-3">
                        Новая папка будет создана внутри: <strong>{activeTab === "photo" ? selectedPhotoFolder : selectedVideoFolder}</strong>
                    </p>
                    <CForm>
                        <CFormLabel>Название папки:</CFormLabel>
                        <CFormInput
                            type="text"
                            placeholder="Введите название папки"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            disabled={creatingFolder}
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateFolder(activeTab);
                                }
                            }}
                        />
                    </CForm>
                </CModalBody>
                <CModalFooter>
                    <CButton 
                        color="secondary" 
                        onClick={() => {
                            setShowFolderModal(false);
                            setNewFolderName("");
                        }}
                        disabled={creatingFolder}
                    >
                        Отмена
                    </CButton>
                    <CButton 
                        color="primary" 
                        onClick={() => handleCreateFolder(activeTab)}
                        disabled={!newFolderName.trim() || creatingFolder}
                    >
                        {creatingFolder ? (
                            <CSpinner size="sm" className="me-2" />
                        ) : (
                            <CIcon icon={cilPlus} className="me-2" />
                        )}
                        Создать папку
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default FileUploader;